import type { RootState } from "../state";
import { nextTick } from "vue";

// UI界面相关方法

// 页面可见性变化监听器
let visibilityChangeListener: (() => void) | null = null;

export function setupVisibilityListener(
  this: RootState & { [key: string]: any }
) {
  // 避免重复绑定
  if (visibilityChangeListener) {
    document.removeEventListener("visibilitychange", visibilityChangeListener);
  }

  visibilityChangeListener = () => {
    console.log(
      `📱 页面可见性变化: hidden=${document.hidden}, currentRoomId=${this.currentRoomId}`
    );

    if (!document.hidden && this.currentRoomId) {
      // 页面恢复可见时，同步所有房间的音频进度
      const roomState = this.roomStates.get(this.currentRoomId);

      console.log(
        `📱 页面变为可见，检查房间状态: isPlaying=${roomState?.isPlaying}, hasHowlerPlayer=${!!(roomState as any)?.howlerPlayer}`
      );

      if (roomState && (roomState as any).howlerPlayer) {
        // 🍎 可见恢复优先：若 WebAudio 仍在可听播放，先让 WA 持续驱动，避免 UI 无进度
        let waAudible = false;
        try {
          const wa: any = (roomState as any).__appleWA__;
          if (wa && wa.player && typeof wa.player.getStatus === "function") {
            const ws = wa.player.getStatus();
            const hasActive = !!(
              ws &&
              typeof ws.activeSourcesCount === "number" &&
              ws.activeSourcesCount > 0
            );
            waAudible = !!(ws && ws.isPlaying && ws.duration > 0 && hasActive);
            if (waAudible) {
              console.log(
                "📱 可见恢复：WA仍在可听播放，暂不切源，维持推进（不主动触发play）"
              );
              try {
                typeof wa.player.setForeHandoff === "function" &&
                  wa.player.setForeHandoff(false);
              } catch {}
              // 不主动触发任何 play()/resume()，遵循无手势不启播策略
            }
          }
        } catch {}
        const currentItem = roomState.queue[roomState.currentIndex];

        console.log(
          `📱 当前播放项目: currentIndex=${roomState.currentIndex}, hasCurrentItem=${!!currentItem}`
        );

        if (
          currentItem &&
          currentItem.text &&
          (roomState as any).howlerPlayer &&
          (roomState as any).howlerPlayer.duration()
        ) {
          // 记录同步前的状态
          const beforeCharCount = currentItem.playedCharCount || 0;

          // 强制同步文字进度到音频实际进度
          // 🔧 修复：通过getStatus()获取时间和时长，而不是直接调用seek()和duration()
          const status = (roomState as any).howlerPlayer.getStatus();
          const currentTime = status.currentTime;
          const totalDuration = status.duration;
          const totalChars = currentItem.text.length;
          const actualPercent = Math.min(currentTime / totalDuration, 1);
          const syncedCharCount = Math.floor(actualPercent * totalChars);

          // console.log(`📱 🔧 同步进度详情:
          //   - 音频时间: ${currentTime.toFixed(2)}s / ${totalDuration.toFixed(2)}s (${(actualPercent * 100).toFixed(1)}%)
          //   - 文字进度: ${beforeCharCount} → ${syncedCharCount} / ${totalChars}
          //   - 是否倒退: ${syncedCharCount < beforeCharCount}
          //   - 播放器状态: paused=${!(roomState as any).howlerPlayer.getStatus().isPlaying}, loaded=${!!(roomState as any).howlerPlayer}`);

          // 🔧 关键修复：防止进度倒退
          if (syncedCharCount < beforeCharCount) {
            // console.log(`📱 ⚠️ 检测到进度倒退，保持原有进度不变`);
          } else {
            currentItem.playedCharCount = syncedCharCount;
            // console.log(`📱 ✅ 进度同步完成`);
          }
        }

        // 🔧 可见后兜底恢复（Howler 分支）：仅当 WA 未在可听播放时介入
        if (!waAudible) {
          try {
            const status = (roomState as any).howlerPlayer.getStatus();
            if (!status.isPlaying) {
              const nearEnd =
                status.duration > 0 &&
                status.currentTime >= Math.max(0, status.duration - 0.3);
              // 遵循不主动触发播放的策略：仅记录状态，不调用 resume()/playNext()
              console.log(
                "📱 可见后(H)：检测到未在播，保持静默（不触发play/resume）",
                { nearEnd, dur: status.duration, cur: status.currentTime }
              );
            }
          } catch {}
        }

        // 🔧 二次兜底（双源自检）：300ms 后复查 WA/H，确保至少一个源在推进
        setTimeout(() => {
          try {
            let hasAudible = false;
            try {
              const wa: any = (roomState as any).__appleWA__;
              if (wa && wa.player) {
                const ws = wa.player.getStatus && wa.player.getStatus();
                const waOk = !!(
                  ws &&
                  ws.isPlaying &&
                  ws.duration > 0 &&
                  ws.activeSourcesCount > 0
                );
                if (waOk) hasAudible = true;
              }
            } catch {}
            try {
              const hs = (roomState as any).howlerPlayer.getStatus();
              const hOk = !!(hs && hs.isPlaying && hs.duration > 0);
              if (hOk) hasAudible = true;
            } catch {}
            if (!hasAudible) {
              // 坚持不主动触发播放：仅记录并等待用户手势/已有路径接管
              console.log(
                "📱 二次兜底(双源)：均未在播，按策略不触发play，等待后续事件"
              );
            }
          } catch {}
        }, 300);

        // 📶 可见后快速自检：音频队列 WebSocket 未连接则立即尝试重连
        try {
          const ws = roomState.audioQueueWs as WebSocket | null;
          const live = roomState.productList?.status === "live";
          const hasSession = !!roomState.sessionId;
          const notOpen = !ws || (ws && ws.readyState !== WebSocket.OPEN);
          if (live && hasSession && notOpen) {
            console.log("📶 可见后自检：音频WS未连接，尝试重连");
            this.initAudioQueueWebSocket(this.currentRoomId!).catch(() => {});
          }
        } catch {}
      }
    }
  };

  document.addEventListener("visibilitychange", visibilityChangeListener);
}

export function removeVisibilityListener() {
  if (visibilityChangeListener) {
    document.removeEventListener("visibilitychange", visibilityChangeListener);
    visibilityChangeListener = null;
  }
}

export function setLiveRoomRef(
  this: RootState & { [key: string]: any },
  ref: any
) {
  if (this.currentRoomState) this.currentRoomState.liveRoomRef = ref;
}

export function setPublicInfoRef(
  this: RootState & { [key: string]: any },
  ref: any
) {
  if (this.currentRoomState && ref) this.currentRoomState.publicInfoRef = ref;
}

export function setQueueRef(
  this: RootState & { [key: string]: any },
  ref: any
) {
  if (this.currentRoomState) this.currentRoomState.queueRef = ref;
}

export function startEdit(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  this.currentRoomState.isEditing = true;
  nextTick(() => {
    if (this.currentRoomState.liveRoomRef) {
      this.currentRoomState.liveRoomRef.focus();
    }
  });
}

export function stopEdit(this: RootState & { [key: string]: any }) {
  if (this.currentRoomState) this.currentRoomState.isEditing = false;
}

export function scrollToBottom(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState || !this.currentRoomState.isNearBottom) return;
  const state = this.currentRoomState;
  if (state.publicInfoRef) {
    state.publicInfoRef.scrollTop = state.publicInfoRef.scrollHeight;
  }
}

export function handleScroll(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;
  const container = state.publicInfoRef;
  if (!container) {
    state.isNearBottom = true;
    return;
  }
  const threshold = 20;
  state.isNearBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight <=
    threshold;
}

export function resetState(this: RootState & { [key: string]: any }) {
  // 此函数现在可以被认为是"销毁"一个房间的状态
  // 或在不再需要时清理
  if (this.currentRoomId && this.roomStates.has(this.currentRoomId)) {
    const state = this.roomStates.get(this.currentRoomId);
    if (state.commentWs) state.commentWs.close();
    if (state.audioQueueWs) state.audioQueueWs.close();
    if ((state as any).howlerPlayer) {
      (state as any).howlerPlayer.pause();
      (state as any).howlerPlayer = null;
    }

    // 我们可能会选择删除它，或者只是重置
    // this.roomStates.delete(this.currentRoomId);
    // this.currentRoomId = null;
  }
}

// 🔇 强制静音所有房间（离开直播间页面时）
export function muteAllRoomsOnPageLeave(
  this: RootState & { [key: string]: any }
) {
  console.log("🔇 离开直播间页面，静音所有房间");
  this.forcemuteAllRooms();

  // 清空当前房间ID，表示用户不在任何直播间页面
  this.currentRoomId = null;

  // 保存状态
  this.saveStateToStorage();
}

// 🔇 强制静音所有房间（立即调用）
export function forcemuteAllRooms(this: RootState & { [key: string]: any }) {
  const roomIds = Array.from(this.roomStates.keys());
  console.log(`🔇 静音所有房间，数量: ${roomIds.length}`);

  // 清理无效的房间状态，只保留有音频播放器的房间
  const activeRooms = [];

  roomIds.forEach(roomId => {
    const state = this.roomStates.get(roomId);
    if (state && (state as any).howlerPlayer) {
      activeRooms.push(roomId);
      console.log(
        `🔇 静音房间${roomId}: paused=${!(state as any).howlerPlayer.getStatus().isPlaying}, muted=${(state as any).howlerPlayer.getStatus().muted}`
      );

      // 只静音，不暂停，让音频在后台继续播放
      (state as any).howlerPlayer.setMuted(true);
      (state as any).howlerPlayer.setVolume(0);
      console.log(`🔇 静音房间${roomId}，后台继续播放`);
    } else {
      // 🔧 修复：不删除房间状态，只是没有音频播放器而已
      // 房间可能刚刚离开，数据需要保留
      console.log(`⏸️ 房间${roomId}没有音频播放器，保留房间状态`);
    }
  });

  console.log(`✅ 实际处理了${activeRooms.length}个活跃房间`);
}

// 开播前预检查：更新预览播放器全局状态
export function setPreviewAudioStatus(
  this: RootState & { [key: string]: any },
  payload: { active: boolean; isPlaying: boolean; lastEvent?: string }
) {
  this.previewAudioActive = !!payload.active;
  this.previewAudioIsPlaying = !!payload.isPlaying;
  if (payload.lastEvent) this.previewAudioLastEvent = payload.lastEvent;
}

// 🚫 页面刷新时断开所有WebSocket连接并暂停功能
export function disconnectAllWebSocketsOnRefresh(
  this: RootState & { [key: string]: any }
) {
  const roomIds = Array.from(this.roomStates.keys());
  console.log(
    `🚫 页面刷新：断开所有房间WebSocket连接并停止所有功能，数量: ${roomIds.length}`
  );

  roomIds.forEach(roomId => {
    const state = this.roomStates.get(roomId);
    if (state) {
      // 1. 停止音频播放
      if ((state as any).howlerPlayer) {
        console.log(`🚫 停止房间${roomId}音频播放`);
        (state as any).howlerPlayer.pause();
        (state as any).howlerPlayer.setMuted(true);
        (state as any).howlerPlayer.setVolume(0);
        // 🔧 修复：Howl实例不支持seek(0)，使用stop()来重置
        (state as any).howlerPlayer.stop();
        // 移除事件监听器
        (state as any).howlerPlayer.off();
        (state as any).howlerPlayer = null;
      }

      // 3. 重置播放状态但保留队列位置信息
      state.isPlaying = false;
      // 🔧 不重置currentIndex，保持队列位置，让WebSocket重连后同步正确的状态
      // state.currentIndex = 0; // 注释掉，保留当前位置
      state.isPreloading = false;

      // 4. 断开公屏WebSocket连接
      if (state.commentWs) {
        console.log(`🚫 断开房间${roomId}公屏WebSocket连接`);
        state.commentWs.close();
        state.commentWs = null;
        state.isManuallyUnbound = true;
      }

      // 5. 断开音频队列WebSocket连接
      if (state.audioQueueWs) {
        console.log(`🚫 断开房间${roomId}音频队列WebSocket连接`);
        state.audioQueueWs.close();
        state.audioQueueWs = null;
      }

      // 6. 清理重连计时器
      if (state.commentWsReconnectTimer) {
        clearTimeout(state.commentWsReconnectTimer);
        state.commentWsReconnectTimer = null;
      }
      if (state.audioQueueWsReconnectTimer) {
        clearTimeout(state.audioQueueWsReconnectTimer);
        state.audioQueueWsReconnectTimer = null;
      }

      // 7. 重置连接状态
      state.isConnecting = false;

      // 8. 标记为需要重新连接（用于进入直播间时恢复）
      state.needsReconnect = true;

      // 9. 标记为页面刷新状态，防止列表页自动恢复功能
      state.isPageRefreshed = true;

      console.log(
        `✅ 房间${roomId}所有功能已停止，WebSocket已断开，标记需要重新连接`
      );
    }
  });

  // 6. 断开全局WebSocket连接
  // 注意：这里使用动态导入避免循环依赖
  import("@/utils/emitter/websocket/globalTimingWebSocket")
    .then(({ globalTimingWebSocket }) => {
      if (globalTimingWebSocket && globalTimingWebSocket.isConnected()) {
        console.log(`🚫 断开全局计时WebSocket连接`);
        globalTimingWebSocket.disconnect();
      }
    })
    .catch(() => {});

  import("@/utils/emitter/websocket/globalBalanceWebSocket")
    .then(({ globalBalanceWebSocket }) => {
      if (globalBalanceWebSocket && globalBalanceWebSocket.isConnected()) {
        console.log(`🚫 断开全局余额WebSocket连接`);
        globalBalanceWebSocket.disconnect();
      }
    })
    .catch(() => {});

  import("@/utils/emitter/websocket/globalLiveRoomSyncWebSocket")
    .then(({ globalLiveRoomSyncWebSocket }) => {
      if (
        globalLiveRoomSyncWebSocket &&
        globalLiveRoomSyncWebSocket.isConnected()
      ) {
        console.log(`🚫 断开全局直播间同步WebSocket连接`);
        globalLiveRoomSyncWebSocket.disconnect();
      }
    })
    .catch(() => {});

  console.log(`✅ 页面刷新：所有WebSocket连接已断开，功能已暂停`);
}

// 🔇 抖音式音频状态控制（所有房间都可以后台播放）
export function updateAudioStateAfterActivation(
  this: RootState & { [key: string]: any }
) {
  const roomIds = Array.from(this.roomStates.keys());

  // 🔧 修复：增强音频权限检查逻辑
  // 检查多个条件来确保音频权限判断的准确性
  const hasAudioPermission =
    this.userHasInteracted || this.autoplayPolicyEnabled;

  // 🔧 修复：添加当前房间检查，确保正确的音频状态
  const currentRoomId = this.currentRoomId;
  // 兼容 Hash 路由与 History 路由两种模式
  const path = window.location.pathname || "";
  const hash = window.location.hash || "";
  const isInLiveRoomDetail =
    (path.includes("/myLiveRoom") && path !== "/myLiveRoom") ||
    hash.includes("/myLiveRoom");

  // 如果currentRoomId为null，说明用户已离开直播间页面，所有房间都应该静音但继续后台播放
  if (currentRoomId === null) {
    console.log("🔇 用户已离开直播间页面，静音所有房间（后台继续播放）");
    roomIds.forEach(roomId => {
      const state = this.roomStates.get(roomId);
      if (state && (state as any).howlerPlayer) {
        (state as any).howlerPlayer.setMuted(true);
        (state as any).howlerPlayer.setVolume(0);
        // 不暂停，让音频在后台继续播放
      }
    });
    return;
  }

  roomIds.forEach(roomId => {
    const state = this.roomStates.get(roomId);
    if (state && (state as any).howlerPlayer) {
      const isCurrentRoom = roomId === currentRoomId;

      if (hasAudioPermission) {
        // 🔧 修复：立即设置音频状态，无延迟
        const isLiveStatus = state.productList?.status === "live";

        if (isCurrentRoom && isInLiveRoomDetail) {
          // 当前直播间且在直播间页面：立即有声播放
          (state as any).howlerPlayer.setMuted(false);
          (state as any).howlerPlayer.setVolume(1);
          console.log(`🔊 设置当前房间${roomId}有声播放`);
        } else {
          // 其他房间：强制静音后台播放
          (state as any).howlerPlayer.setMuted(true);
          (state as any).howlerPlayer.setVolume(0);
          console.log(`🔇 强制设置非当前房间${roomId}静音后台播放`);
        }

        // 修复：移除可能会干扰音量设置的兜底逻辑
        // 严格按照 isCurrentRoom 来控制音量，不允许例外
        console.log(
          `房间${roomId}: isCurrentRoom=${isCurrentRoom}, muted=${(state as any).howlerPlayer.getStatus().muted}, volume=${(state as any).howlerPlayer.getStatus().volume}`
        );

        // 🔧 修复：只有当前房间且在直播间页面时才播放音频
        if (
          isCurrentRoom &&
          isInLiveRoomDetail &&
          (state as any).howlerPlayer &&
          !(state as any).howlerPlayer.getStatus().isPlaying &&
          isLiveStatus
        ) {
          (state as any).howlerPlayer.play();
        }
      } else {
        // 无权限时，所有房间静音播放
        if ((state as any).howlerPlayer) {
          (state as any).howlerPlayer.setMuted(true);
          (state as any).howlerPlayer.setVolume(0);
        }
      }

      // 🔧 修复：重置需要用户交互的标记
      // 旧播放器属性已移除，使用 Howler.js 统一管理
    }
  });

  // 🔧 修复：同时更新WebAudio播放器的房间隔离状态
  try {
    if (typeof (this as any).updateAppleWebAudioRoomIsolation === "function") {
      (this as any).updateAppleWebAudioRoomIsolation();
    }
  } catch (e) {
    console.warn("更新WebAudio房间隔离状态失败:", e);
  }
}

// 🔧 简化：移除复杂的焦点处理，音频状态只在播放时判断
export function setupPageChangeListener(
  this: RootState & { [key: string]: any }
) {
  // 保持简单：只保存状态，不处理音频
  window.addEventListener(
    "beforeunload",
    () => {
      this.saveStateToStorage();
    },
    { passive: true }
  );
}

// 🔇 设置全局用户交互监听器（抖音式无感知播放）
export function setupGlobalInteractionListener(
  this: RootState & { [key: string]: any }
) {
  // 如果已经有用户交互权限，直接返回
  if (this.userHasInteracted) {
    return;
  }

  // 监听多种用户交互事件
  const interactionEvents = [
    "click",
    "touchstart",
    "keydown",
    "scroll",
    "mousemove",
    "mousedown"
  ];

  const handleUserInteraction = () => {
    // 标记用户已交互
    this.userHasInteracted = true;

    // 🔧 防护：只在真正需要时更新音频状态
    // 旧播放器属性已移除，使用 Howler.js 统一管理
    const needsUpgrade = false;

    if (needsUpgrade) {
      console.log(`🎯 用户交互事件触发，更新音频状态`);
      // 更新音频状态
      this.updateAudioStateAfterActivation();

      // 恢复所有需要用户交互的房间
      // 旧播放器属性已移除，使用 Howler.js 统一管理
    } else {
      console.log(`🎯 用户交互事件触发，但无需更新音频状态（已正常播放）`);
    }

    // 移除所有监听器
    interactionEvents.forEach(eventName => {
      document.removeEventListener(eventName, handleUserInteraction, true);
    });
  };

  // 添加事件监听器
  interactionEvents.forEach(eventName => {
    document.addEventListener(eventName, handleUserInteraction, {
      once: true,
      capture: true,
      passive: true
    });
  });
}

// 抖音式无感知播放核心策略
export function setupDouYinStyleAutoplay(
  this: RootState & { [key: string]: any }
) {
  // 🔧 防护：如果用户已经交互过，不再重复设置
  if (this.userHasInteracted) {
    console.log(`🎯 用户已交互，跳过抖音式自动播放设置`);
    return;
  }

  // 策略1: 微信环境特殊处理
  if (typeof (window as any).WeixinJSBridge !== "undefined") {
    (window as any).WeixinJSBridge.invoke("getNetworkType", {}, () => {
      if (!this.userHasInteracted) {
        this.userHasInteracted = true;
      }
    });
    return;
  }

  // 策略2: 监听微信JSBridge准备就绪
  document.addEventListener(
    "WeixinJSBridgeReady",
    () => {
      if (!this.userHasInteracted) {
        this.userHasInteracted = true;
      }
    },
    false
  );

  // 策略3: 使用Document Visibility API
  if (document.visibilityState === "visible") {
    setTimeout(() => {
      if (!this.userHasInteracted) {
        this.userHasInteracted = true;
        this.updateAudioStateAfterActivation();
      }
    }, 200);
  }

  // 策略4: 触摸滚动激活（增加防护逻辑）
  let scrollActivated = false;
  const scrollHandler = () => {
    if (!scrollActivated && !this.userHasInteracted) {
      scrollActivated = true;
      this.userHasInteracted = true;
      console.log(`🎯 滚动事件触发音频激活`);

      // 移除滚动监听器，避免重复触发
      window.removeEventListener("scroll", scrollHandler);
      window.removeEventListener("touchmove", scrollHandler);
    }
  };

  window.addEventListener("scroll", scrollHandler, { passive: true });
  window.addEventListener("touchmove", scrollHandler, { passive: true });

  // 策略5: 利用页面焦点事件
  const focusHandler = () => {
    if (!this.userHasInteracted) {
      this.userHasInteracted = true;
      console.log(`🎯 焦点事件触发音频激活`);
      window.removeEventListener("focus", focusHandler);
    }
  };

  window.addEventListener("focus", focusHandler);
}

export function showInteractionOverlay(
  this: RootState & { [key: string]: any }
) {
  // 检查是否有等待激活的房间
  // 旧播放器属性已移除，使用 Howler.js 统一管理
  const waitingRooms: [string, any][] = [];

  if (waitingRooms.length === 0) {
    return;
  }

  const overlay = document.createElement("div");
  overlay.id = "audio-activation-overlay";
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 10000;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;

  // 创建提示内容
  const content = document.createElement("div");
  content.style.cssText = `
    background: white;
    padding: 30px 40px;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    text-align: center;
    max-width: 400px;
    pointer-events: none;
  `;

  content.innerHTML = `
    <div style="font-size: 48px; margin-bottom: 16px;">🎵</div>
    <h3 style="margin: 0 0 12px 0; font-size: 18px; color: #333;">启用音频播放</h3>
    <p style="margin: 0 0 16px 0; color: #666; font-size: 14px; line-height: 1.5;">
      检测到 ${waitingRooms.length} 个直播间需要激活音频播放
    </p>
    <p style="margin: 0; color: #999; font-size: 12px;">
      点击任意位置继续
    </p>
  `;

  overlay.appendChild(content);

  const handleActivation = () => {
    // 标记用户已交互
    this.userHasInteracted = true;

    // 激活所有等待的房间
    // 旧播放器属性已移除，使用 Howler.js 统一管理

    // 移除覆盖层
    if (overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
  };

  overlay.addEventListener("click", () => handleActivation(), {
    once: true
  });
  overlay.addEventListener("touchstart", () => handleActivation(), {
    once: true
  });

  // 键盘激活
  const keyHandler = (_e: KeyboardEvent) => {
    handleActivation();
    document.removeEventListener("keydown", keyHandler);
  };
  document.addEventListener("keydown", keyHandler);

  document.body.appendChild(overlay);

  // 15秒后自动移除覆盖层
  setTimeout(() => {
    if (overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
      document.removeEventListener("keydown", keyHandler);
    }
  }, 15000);
}

// 🔧 删除了复杂的焦点处理逻辑，保持音频状态判断的简单性
